// @system — copy for the blog: the public index/article pages and the admin blog area
// (/app/admin/blog). Read as text.blog.*; products override keys in @custom/text (deep-merged,
// arrays replaced whole). `{name}`-style placeholders are filled where the string is used.
export const blog = {
  // ── Public pages ─────────────────────────────────────────────────────────
  title: 'Blog',
  subtitle: 'Product updates, engineering deep-dives, design thinking, and more.',
  /** Category pills of /blog; the first entry is the "everything" filter. */
  categories: ['All', 'Product', 'Engineering', 'Design', 'Company', 'Tutorials'],
  readMore: 'Read more',
  readArticle: 'Read article',
  morePosts: 'More posts',
  minRead: '{minutes} min read',
  coverAlt: 'Cover image for {title}',
  backToBlog: 'Back to Blog',
  allPosts: 'All posts',
  moreIn: 'More in {category}',
  onThisPage: 'On this page',
  share: {
    label: 'Share this article',
    copy: 'Copy link',
    copied: 'Link copied',
    x: 'Share on X',
    linkedin: 'Share on LinkedIn',
  },

  // ── Admin area (/app/admin/blog) ─────────────────────────────────────────
  admin: {
    title: 'Blog',
    description: 'Write, generate, schedule and publish articles in your brand.',
    tabs: {
      posts: 'Posts',
      editor: 'Editor',
      generate: 'Generate',
      settings: 'Settings',
      integrations: 'Integrations',
    },
    common: {
      save: 'Save',
      saving: 'Saving…',
      cancel: 'Cancel',
      back: 'Back',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      loadError: 'Something went wrong while loading this page.',
      retry: 'Retry',
      remove: 'Remove {item}',
    },
    status: {
      all: 'All',
      draft: 'Draft',
      published: 'Published',
      scheduled: 'Scheduled',
      archived: 'Archived',
    },
    source: {
      manual: 'Manual',
      generated: 'Generated',
      api: 'API',
      seed: 'Seed',
    },
    posts: {
      newPost: 'Write a post',
      generate: 'Generate with AI',
      filterLabel: 'Filter by status',
      emptyTitle: 'No blog posts yet',
      emptyBody: 'Write your first article or let the generator draft one from your brand and topics.',
      emptyFiltered: 'No posts match this filter',
      publish: 'Publish',
      unpublish: 'Unpublish',
      published: 'Post published',
      unpublished: 'Post moved back to draft',
      deleted: 'Post deleted',
      deleteTitle: 'Delete this post?',
      deleteBody: '"{title}" will be removed permanently, including its images. This cannot be undone.',
      featured: 'Featured',
      actionFailed: 'Could not complete that action',
    },
    editor: {
      newTitle: 'New post',
      editTitle: 'Edit post',
      saved: 'Post saved',
      titleRequired: 'Title is required',
      fields: {
        title: 'Title',
        slug: 'Slug',
        excerpt: 'Excerpt',
        category: 'Category',
        author: 'Author',
        tags: 'Tags (comma-separated)',
        status: 'Status',
        scheduledAt: 'Publish on',
        featured: 'Featured post',
        keyword: 'Primary keyword',
        canonicalUrl: 'Canonical URL',
        metaTitle: 'Meta title',
        metaDescription: 'Meta description',
        content: 'Content (Markdown)',
      },
      placeholders: {
        title: 'Post title',
        excerpt: 'Short summary shown on cards and in search results',
        tags: 'engineering, security',
        keyword: 'e.g. invoice automation',
        canonicalUrl: 'https://… (only when this article was first published elsewhere)',
        content: '## Write your post…',
      },
      seoSection: 'SEO metadata',
      cover: {
        title: 'Cover image',
        description: '1600×900 hero shown at the top of the article, on cards and on social previews.',
        upload: 'Upload file',
        uploadHint: 'JPG, PNG or WebP up to 8 MB.',
        pasteUrl: 'Image URL',
        useUrl: 'Use URL',
        regenerate: 'Regenerate with AI',
        regenerateQueued: 'Image regeneration queued: refresh the post in a moment.',
        uploaded: 'Cover updated',
        tooLarge: 'That file is larger than 8 MB.',
        notImage: 'Please choose an image file.',
        saveFirst: 'Save the post before adding a cover image.',
        none: 'No cover yet: a branded placeholder is shown until one is added.',
        alt: 'Current cover image',
      },
      preview: {
        show: 'Show preview',
        hide: 'Hide preview',
        title: 'Preview',
        empty: 'Start writing to see the rendered article here.',
        failed: 'Preview unavailable',
      },
      gallery: {
        title: 'Images in this post',
        description: 'Alt text is read by screen readers and search engines. Describe what the image shows.',
        alt: 'Alt text for image {index}',
        empty: 'No images yet. Figures appear here after generation or upload.',
      },
      checklist: {
        title: 'SEO checklist',
        titleLength: 'Title is 60 characters or fewer',
        metaLength: 'Meta description is 150–160 characters',
        keyword: 'Primary keyword appears in the title or content',
        cover: 'Post has a cover image',
      },
    },
    generate: {
      title: 'Generate an article',
      description: 'The generator plans, writes, checks SEO and illustrates a post in your brand voice. Leave the topic blank to let it pick from your settings.',
      topic: 'Topic',
      topicPlaceholder: 'e.g. How small teams keep invoices on time',
      keyword: 'Primary keyword (optional)',
      keywordPlaceholder: 'e.g. invoice reminders',
      publish: 'Publish immediately',
      publishHint: 'Otherwise the article is saved as a draft for review.',
      submit: 'Generate article',
      submitting: 'Queuing…',
      queued: 'Generation queued: it usually takes a couple of minutes.',
      alreadyRunning: 'A generation is already running. Wait for it to finish before starting another.',
      failed: 'Could not start the generation',
      noProvider: 'No text provider is configured yet. Connect one under Settings before generating.',
      runs: {
        title: 'Recent runs',
        empty: 'No runs yet.',
        status: 'Status',
        topic: 'Topic',
        cost: 'Cost',
        duration: 'Duration',
        post: 'Post',
        openPost: 'Open post',
        viewPost: 'View',
        live: '{count} run(s) in progress',
        idle: 'No run in progress',
        trigger: {
          bootstrap: 'First article',
          schedule: 'Scheduled',
          manual: 'Manual',
          api: 'API',
        },
        statuses: {
          queued: 'Queued',
          running: 'Running',
          done: 'Done',
          failed: 'Failed',
        },
      },
    },
    settings: {
      title: 'Automation settings',
      description: 'How often articles are generated, in which voice, and which providers do the work.',
      saved: 'Settings saved',
      saveFailed: 'Could not save settings',
      enabled: 'Automatic generation',
      enabledHint: 'When on, new articles are generated on the cadence below.',
      cadence: 'Cadence',
      cadences: {
        daily: 'Daily',
        weekdays: 'Weekdays',
        weekly: 'Weekly',
        manual: 'Manual only',
      },
      publishHour: 'Publish hour (0–23)',
      timezone: 'Timezone',
      autoPublish: 'Publish generated articles automatically',
      autoPublishHint: 'Off = generated articles wait as drafts for your review.',
      topics: 'Topics',
      topicsHint: 'Press Enter or type a comma to add one.',
      keywords: 'Keywords',
      audience: 'Audience',
      audiencePlaceholder: 'Who reads this blog? e.g. founders of 5–50 person service businesses',
      tone: 'Tone',
      tones: {
        professional: 'Professional',
        educational: 'Educational',
        'thought-leadership': 'Thought leadership',
      },
      wordMin: 'Minimum words',
      wordMax: 'Maximum words',
      defaultAuthor: 'Default author',
      defaultCategory: 'Default category',
      categories: 'Categories',
      internalLinks: 'Internal links',
      internalLinksHint: 'Pages of your product the generator may link to from an article.',
      linkPath: 'Path',
      linkLabel: 'Label',
      addLink: 'Add link',
      providers: 'Providers',
      textProvider: 'Text provider',
      textModel: 'Text model (optional)',
      imageProvider: 'Image provider',
      imageModel: 'Image model (optional)',
      auto: 'Automatic (first configured)',
      notConfigured: 'not configured',
      noTextProvider: 'No text provider is configured on the server. Add an ORKOSI, ANTHROPIC or OPENAI key to enable generation.',
      noImageProvider: 'No image provider is configured. Articles get branded SVG illustrations.',
      noStorage: 'No file storage is configured. Images are kept on the local disk of this server.',
      providerNames: {
        orkosi: 'Orkosi',
        anthropic: 'Anthropic',
        openai: 'OpenAI',
        replicate: 'Replicate',
        svg: 'Branded SVG (always available)',
      },
    },
    integrations: {
      title: 'Publish from other tools',
      description: 'SEO services and content pipelines (rankarilla.com-style) can push articles straight into this blog with an API key.',
      endpoint: 'Endpoint',
      scope: 'Required scope',
      scopeHint: 'Create a key with the {scope} scope (or write / *). Reads need blog:read.',
      createKey: 'Create an API key',
      example: 'Example request',
      body: 'Request body',
      bodyHint: 'Send markdown in content or ready HTML in content_html (sanitized server-side). Repeated pushes with the same external_source + external_id update the post instead of duplicating it. Set generate_images to true to get a branded hero and social image when no cover_image_url is given.',
      copy: 'Copy',
      copied: 'Copied',
      fields: {
        title: 'Required.',
        content: 'Markdown body (or content_html).',
        status: "'draft' (default), 'published' or 'scheduled' (+ scheduled_at).",
        external: 'Your own ids: make re-pushes idempotent.',
      },
    },
  },
}
